import React, { useState, useEffect } from "react";
import { NavLink, useMatch, generatePath, useParams } from "react-router-dom";
import { NavRouteObject } from "@/routes";
import { type RouteId } from "@/routes/getRoute";
import { ProtectedNavLink } from "@/components/ProtectedNavigationLink";
import { FilterState } from "../FilterSidebar/FilterState";
import {
  FEATURE_PROTECTED_ROUTES,
  PARTNERSHIP_PROTECTED_ROUTES,
} from "@/routes/protectedRoutes";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/Collapsible";

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarGroupContent,
  useSidebar,
} from "@/components/ui/Sidebar";

import {
  PanelsTopLeft,
  ChartSpline,
  Hammer,
  Users,
  DatabaseZap,
  ChevronDown,
  BookMarked,
  Settings,
  MessageCircle,
  GraduationCap,
} from "lucide-react";
import { RiseStar } from "@/assets/svg/RiseStar";
import { RiseLogo } from "@/assets/svg/RiseLogo";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { ROUTES } from "@/routes/paths";
import { PartnerFeatureEnum, StudentTypeEnum } from "@/__generated__/types";
import { UrlQueryObject } from "@/lib/UrlSerializers";
import { buildUrlState } from "@/hooks/useUrlSerializer";
import { useCurrentCohortYear } from "@/hooks/useCurrentCohortYear";

const NAV_ICONS = {
  [ROUTES.DASHBOARD]: <PanelsTopLeft />,
  [ROUTES.RISE.BASE]: <RiseStar />,
  [ROUTES.ANALYTICS.BASE]: <ChartSpline />,
  [ROUTES.STUDENTS]: <GraduationCap />,
  [ROUTES.COMMUNITY_TOOLS.BASE]: <Hammer />,
  [ROUTES.COMMUNITY_MANAGEMENT.BASE]: <Users />,
  [ROUTES.DATA_MANAGEMENT.BASE]: <DatabaseZap />,
  [ROUTES.RESOURCES]: <BookMarked />,
  [ROUTES.SETTINGS.BASE]: <Settings />,
  [ROUTES.CHAT.INDEX]: <MessageCircle />,
};

const SHOULD_HIDE_SECTIONS = {
  [ROUTES.RISE.BASE]: (
    hasOrgFeature: (flag: PartnerFeatureEnum) => boolean,
  ) => {
    return !hasOrgFeature(PartnerFeatureEnum.Rise);
  },
};

const useGetRouteQueryParamsMap = (): Partial<
  Record<RouteId, UrlQueryObject>
> => {
  const { cohortYear } = useCurrentCohortYear();

  return {
    [ROUTES.ANALYTICS.FUNNEL]: {
      ...buildUrlState("FUNNEL_CHART")({
        compareFilterState: undefined,
        filterState: new FilterState({
          studentType: StudentTypeEnum.FirstYear,
          cohortYear: cohortYear ? [`${cohortYear}`] : undefined,
        }),
      }),
    },
  };
};

const RISE_ENHANCED: RouteId[] = ["predictiveModels", "students", "funnel"];

const TITLE_OVERRIDES = {
  [ROUTES.RISE.BASE]: <RiseLogo />,
};

const useIsActive = (relativePath: string) => {
  const matchSubPaths = relativePath !== "";

  return !!useMatch({
    caseSensitive: false,
    end: !matchSubPaths,
    path: `${ROUTES.ORGANIZATION}/${relativePath}${matchSubPaths ? "/*" : ""}`,
  });
};

const useGetFullPath = (relativePath: string) => {
  const params = useParams();
  return generatePath(`${ROUTES.ORGANIZATION}/${relativePath}/`, params);
};

const hasPermissionRequirements = (routeId: RouteId): boolean => {
  const requiredFeature = FEATURE_PROTECTED_ROUTES[routeId];
  const requiredPartnership = PARTNERSHIP_PROTECTED_ROUTES[routeId];

  return !!(requiredFeature || requiredPartnership);
};

const shouldShowRiseStar = (
  item: NavRouteObject,
  hasOrgFeature: (flag: PartnerFeatureEnum) => boolean,
): boolean => {
  const routeId = item.handle?.routeId;
  if (!routeId) return false;
  return (
    routeId &&
    RISE_ENHANCED.includes(routeId) &&
    hasOrgFeature(PartnerFeatureEnum.Rise)
  );
};

export const NavigationLinks = ({
  navRoutes,
}: {
  navRoutes: NavRouteObject[];
}) => {
  const { open } = useSidebar();
  const { hasOrgFeature } = useOrgPermissions();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    if (!open) {
      setExpandedSections(new Set());
    }
  }, [open]);

  const toggleExpand = (expanded: boolean, key: string) =>
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (expanded) {
        newSet.add(key);
      } else {
        newSet.delete(key);
      }
      return newSet;
    });

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        {/* TODO: Remove this when we re-enable rise routes */}
        <RiseHeader />
        <SidebarMenu>
          {navRoutes.map((item) => {
            const expandKey = item.path || "";
            return !item.children ? (
              <MenuItem
                key={expandKey}
                item={item}
                hasOrgFeature={hasOrgFeature}
              />
            ) : (
              <SubMenu
                key={item.path}
                item={item}
                expanded={expandedSections.has(expandKey)}
                onToggleExpand={(expanded) => toggleExpand(expanded, expandKey)}
                hasOrgFeature={hasOrgFeature}
              />
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

const MenuItem = ({
  item,
  hasOrgFeature,
}: {
  item: NavRouteObject;
  hasOrgFeature: (flag: PartnerFeatureEnum) => boolean;
}) => {
  const path = item.path || "";
  const icon = NAV_ICONS[path];

  const isActive = useIsActive(path);
  const fullPath = useGetFullPath(path);

  const { setOpen: setSidebarOpen } = useSidebar();
  const buttonTitle = item.handle?.name;

  if (shouldHideSection(hasOrgFeature, item.path)) return;

  if (item.path !== undefined || item.index) {
    const routeId = item.handle?.routeId;
    const isProtected = routeId && hasPermissionRequirements(routeId);
    const showRiseStar = shouldShowRiseStar(item, hasOrgFeature);

    const content = (
      <>
        {icon && (
          <div className="[&>svg]:size-4" aria-hidden={true}>
            {icon}
          </div>
        )}
        <div className="h-4 truncate leading-tight">{buttonTitle}</div>
        {showRiseStar && <RiseStar />}
      </>
    );

    return (
      <SidebarMenuItem key={item.path}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          tooltip={buttonTitle}
          onClick={() => setSidebarOpen(true)}
        >
          {isProtected && routeId ? (
            <ProtectedNavLink route={routeId}>{content}</ProtectedNavLink>
          ) : (
            <NavLink to={fullPath}>{content}</NavLink>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }
};

const SubMenu = ({
  item,
  onToggleExpand,
  expanded,
  hasOrgFeature,
}: {
  item: NavRouteObject;
  onToggleExpand: (expanded: boolean) => void;
  expanded: boolean;
  hasOrgFeature: (flag: PartnerFeatureEnum) => boolean;
}) => {
  const { setOpen: setSidebarOpen } = useSidebar();
  if (!item.children) return;
  const icon = item.path ? NAV_ICONS[item.path] : undefined;

  if (shouldHideSection(hasOrgFeature, item.path)) return;

  const navChildren = item.children.filter(
    (child) => child.path && child.handle,
  );

  const toggleSubmenu = (open: boolean) => {
    onToggleExpand(open);
    if (open) setSidebarOpen(true);
  };

  const headerId = `menu-button-${item.path}`;
  const buttonTitle = item.handle?.name;
  return (
    <SidebarMenuItem key={item.path}>
      <Collapsible
        open={expanded}
        onOpenChange={toggleSubmenu}
        className="group/collapsible"
      >
        <h3 role="heading" aria-level={2}>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton id={headerId} tooltip={buttonTitle}>
              {icon && (
                <div className="[&>svg]:size-4" aria-hidden={true}>
                  {icon}
                </div>
              )}
              <span className="truncate">
                {(item.path && TITLE_OVERRIDES[item.path]) || buttonTitle}
              </span>
              <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
        </h3>
        <CollapsibleContent
          className="CollapsibleContent"
          role="region"
          aria-labelledby={headerId}
        >
          <SidebarMenuSub className="mr-0 pr-1">
            {navChildren.map((child: NavRouteObject) => {
              return (
                <SubMenuItem
                  key={child.path}
                  pathPrefix={item.path}
                  child={child}
                  hasOrgFeature={hasOrgFeature}
                />
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  );
};

const SubMenuItem = ({
  pathPrefix = "",
  child,
  hasOrgFeature,
}: {
  pathPrefix?: string;
  child: NavRouteObject;
  hasOrgFeature: (flag: PartnerFeatureEnum) => boolean;
}) => {
  const path = `${pathPrefix}/${child.path}`;
  const isActive = useIsActive(path);
  const fullPath = useGetFullPath(path);

  const routeId = child.handle?.routeId;
  const isProtected = routeId && hasPermissionRequirements(routeId);
  const showRiseStar = shouldShowRiseStar(child, hasOrgFeature);
  const queryParamsMap = useGetRouteQueryParamsMap();

  const content = (
    <div className="flex w-full items-center">
      <span className="truncate">{child.handle?.name}</span>
      {showRiseStar && <RiseStar className="ml-2 flex-shrink-0" />}
    </div>
  );

  return (
    <SidebarMenuSubItem key={path}>
      <SidebarMenuButton asChild isActive={isActive}>
        {isProtected && routeId ? (
          <ProtectedNavLink query={queryParamsMap[routeId]} route={routeId}>
            {content}
          </ProtectedNavLink>
        ) : (
          <NavLink to={fullPath}>{content}</NavLink>
        )}
      </SidebarMenuButton>
    </SidebarMenuSubItem>
  );
};

const shouldHideSection = (
  hasOrgFeature: (flag: PartnerFeatureEnum) => boolean,
  path?: string,
) => {
  return (
    path &&
    SHOULD_HIDE_SECTIONS[path] &&
    SHOULD_HIDE_SECTIONS[path](hasOrgFeature)
  );
};

// Temporary static rise logo
const RiseHeader = () => {
  const { hasOrgFeature } = useOrgPermissions();
  const hasRise = hasOrgFeature(PartnerFeatureEnum.Rise);

  return (
    hasRise && (
      <div className="mb-1 flex items-center gap-2 p-2">
        <RiseStar className="shrink-0" />
        <span className="shrink truncate">
          <RiseLogo />
        </span>
      </div>
    )
  );
};
